import { Page, SaveResponse, SearchRequest } from '@hclinear/hc-linear-react';
import { ProductSnDTO } from '@model/productSn/ProductSnDTO';
import { StopProductSnDTO } from '@model/productSn/StopProductSnDTO';
import { VolumeAndBacklightDTO } from '@model/productSn/VolumeAndBacklightDTO';
import {
  ChangeServerRequest,
  ChangeServerResponse,
  DashboardDTO,
  PreviewRequest,
  TerminalLogDetailDTO,
  TerminalLogDTO,
  TerminalLogSearchRequest,
  TerminalManualQueueDTO,
  TerminalManualResponse,
  TerminalPreviewDTO,
  TerminalQueueModeChangeRequest,
  TerminalStatusBriefDTO,
  TerminalStatusDTO,
  TerminalStatusFilterDTO,
} from '@/model';
import {
  apiGenericDelete,
  apiGenericGet,
  apiGenericPost,
  apiGenericSave,
} from '@/service';
import { DeleteResponse, GenericPostResponse } from '@/type';
import { getApiUrl, getApiUrlWithParams } from '@/util';

const BASE_URL = '/api/v1/product-sn/';

export const productSnChangeFavoriteAPI = (
  hwId: number,
): Promise<GenericPostResponse<DashboardDTO>> =>
  apiGenericGet(getApiUrl(BASE_URL, `changeFavorite/${hwId}`));

export const getTerminalPreviewAPI = (
  request: PreviewRequest,
): Promise<GenericPostResponse<TerminalPreviewDTO[]>> =>
  apiGenericPost(getApiUrl(BASE_URL, `getTerminalPreview`), request, true);

export const listProductSnAPI = (): Promise<ProductSnDTO[]> =>
  apiGenericGet(getApiUrl(BASE_URL, 'list'));

export const getProductSnByIdAPI = (id: number): Promise<ProductSnDTO> =>
  apiGenericGet(getApiUrl(BASE_URL, `load/${id}`));

export const listAvailableProductSnsAPI = (
  productSnId: number,
): Promise<ProductSnDTO[]> =>
  apiGenericGet(getApiUrlWithParams(BASE_URL, `list/available`, { productSnId }));

export const listProductSnByStopIdAPI = (stopId: number): Promise<StopProductSnDTO[]> =>
  apiGenericGet(getApiUrl(BASE_URL, `listByStop/${stopId}`));

export const searchTerminalStatusAPI = (
  request: SearchRequest<Partial<TerminalStatusFilterDTO>>,
): Promise<GenericPostResponse<Page<TerminalStatusDTO>>> =>
  apiGenericPost(getApiUrl(BASE_URL, 'searchTerminalStatus'), request, true);

export const resetTerminalAPI = (hwId: string): Promise<void> =>
  apiGenericGet(getApiUrl(BASE_URL, `reset/${hwId}`));

export const startInventoryAPI = (hwId: string): Promise<void> =>
  apiGenericGet(getApiUrl(BASE_URL, `inventory/${hwId}`));

export const saveProductSnAPI = (
  request: ProductSnDTO,
): Promise<GenericPostResponse<number>> =>
  apiGenericPost(getApiUrl(BASE_URL, 'save'), request);

export const deleteProductSnAPI = (id: number): DeleteResponse => {
  return apiGenericDelete(getApiUrl(BASE_URL, 'remove'), 'id', id);
};

export const loadProductSnStatusByHwIdApi = (
  hwId: string,
): Promise<TerminalStatusBriefDTO> => {
  return apiGenericGet(getApiUrl(BASE_URL, `loadStatusByHwId/${hwId}`));
};

export const productSnChangeServerAPI = (
  request: ChangeServerRequest,
): Promise<GenericPostResponse<ChangeServerResponse>> => {
  return apiGenericPost(getApiUrl(BASE_URL, 'changeServer'), request);
};

export const searchProductLogsAPI = (
  request: Partial<TerminalLogSearchRequest>,
): Promise<GenericPostResponse<TerminalLogDTO[]>> => {
  return apiGenericPost(getApiUrl(BASE_URL, 'searchProductLogs'), request, true);
};

export const listLogDetailsAPI = (id: number): Promise<TerminalLogDetailDTO[]> => {
  return apiGenericGet(getApiUrl(BASE_URL, `listLogDetails/${id}`));
};

const buildManualFormData = (productSnId: number, file: File): FormData => {
  const formData = new FormData();
  formData.append('productSnId', String(productSnId));
  formData.append('file', file, file.name);

  return formData;
};

export const previewManualAPI = (
  productSnId: number,
  file: File,
): Promise<GenericPostResponse<TerminalManualResponse>> =>
  apiGenericPost(
    getApiUrl(BASE_URL, 'previewManual'),
    buildManualFormData(productSnId, file),
    true,
  );

export const uploadManualAPI = (
  productSnId: number,
  file: File,
): Promise<GenericPostResponse<TerminalManualResponse>> =>
  apiGenericPost(
    getApiUrl(BASE_URL, 'uploadManual'),
    buildManualFormData(productSnId, file),
  );

export const listQueueByProductSnIdAPI = (
  productSnId: number,
): Promise<TerminalManualQueueDTO[]> =>
  apiGenericGet(getApiUrl(BASE_URL, `listQueueByProductSnId/${productSnId}`));

export const changeQueueModeAPI = (
  request: TerminalQueueModeChangeRequest,
): Promise<GenericPostResponse<void>> =>
  apiGenericPost(getApiUrl(BASE_URL, 'changeQueueMode'), request);

export const generateCsvFromProductLogsAPI = (
  request: Partial<TerminalLogSearchRequest>,
): Promise<GenericPostResponse<{ csvFile: string }>> => {
  return apiGenericPost(getApiUrl(BASE_URL, 'generateCsv'), request, true);
};

export const getVolumeAndBacklightAPI = (hwId: string): Promise<VolumeAndBacklightDTO> =>
  apiGenericGet(getApiUrl(BASE_URL, `getVolumeAndBacklight/${hwId}`));

export const saveVolumeAndBacklightAPI = (
  request: Partial<VolumeAndBacklightDTO>,
): SaveResponse => apiGenericSave(getApiUrl(BASE_URL, 'saveVolumeAndBacklight'), request);

export const setVComAPI = (request: Partial<VolumeAndBacklightDTO>): SaveResponse =>
  apiGenericSave(getApiUrl(BASE_URL, 'setVCom'), request);

export const showUserSettingsAPI = (hwId: string): Promise<void> =>
  apiGenericGet(getApiUrl(BASE_URL, `showUserSettings/${hwId}`));
